import React, {useCallback, useEffect, useState} from 'react';
import {
  Col,
  Container,
  Row,
  Button,
  NavItem,
  Navbar,
  NavbarToggler,
  Collapse,
  Nav,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  NavLink
} from 'reactstrap';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import ReactMarkdown from 'react-markdown';
import classNames from 'classnames';
import {Link} from 'react-scroll';
import {faCloudArrowDown} from '@fortawesome/pro-regular-svg-icons/faCloudArrowDown';
import {faExpand} from '@fortawesome/pro-regular-svg-icons/faExpand';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';
import track from 'utils/track';

import SectionCL from 'components/ComponentLibrary/SectionCL';
import Video from 'components/Video';
import CountdownTimer from 'components/Elements/CountdownTimer';
import CheckoutButton from 'components/CheckoutButton';
import {SalesDisclaimer} from 'components/SalesCta';
import {ImgCard, VerticalCard} from 'components/Elements/ImgBullet';
import {ListIcon} from 'components/Elements/IconList';
import Modal from 'components/Modal';
import {PillarModal, PillarModulesCondensed} from 'components/ModuleAccordion';
import FaqList from 'components/FaqList';
import {ListReferenceItem, ListReferences} from 'components/Elements/ListReference';
import {TestimonialBubble} from 'components/Testimonial';

//@ts-ignore
import {imagineList, iconList, references} from 'data/pbcc-content.json';
//@ts-ignore
import {bonus} from 'data/bonus.json';
//@ts-ignore
import {speakers} from 'data/module.json';
//@ts-ignore
import {studentSpotlight} from 'data/testimonials.json';

export function SalesNavigation({className = undefined}) {
  const [collapsed, setCollapsed] = useState(true);
  const toggleNavbar = () => setCollapsed(!collapsed);

  function StickyNavLink({scrollToRef, linkText}) {
    return (
      <NavItem>
        <Link
          className="pbcc-nav-list-item"
          activeClass="active"
          spy={true}
          offset={-75}
          to={scrollToRef}
          smooth={true}
          href="#"
        >
          {linkText}
        </Link>
      </NavItem>
    );
  }
  return (
    <div className={classNames(`pbcc-nav`, className)}>
      <Container>
        <Navbar className={classNames(`p-0`, `navbar-light`, `justify-content-end`)} expand="xl">
          <NavbarToggler onClick={toggleNavbar} />
          <Collapse className="" isOpen={!collapsed} navbar>
            <Nav className="flex-grow-1" navbar>
              <StickyNavLink scrollToRef="pbcc-cta-1" linkText="Apply Now" />
              <StickyNavLink scrollToRef="pbcc-spotlight" linkText="Student Testimonials" />
              <StickyNavLink scrollToRef="why" linkText="Why this program?" />
              <StickyNavLink scrollToRef="events" linkText="Events" />
              <UncontrolledDropdown nav inNavbar>
                <DropdownToggle className="pbcc-nav-list-item" nav caret>
                  Curriculum & Faculty
                </DropdownToggle>
                <DropdownMenu right>
                  <StickyNavLink scrollToRef="curriculum" linkText="Overview" />
                  <StickyNavLink scrollToRef="pillar-1" linkText="Pillar 1: Nutrition" />
                  <StickyNavLink scrollToRef="pillar-2" linkText="Pillar 2: Coaching" />
                  <StickyNavLink scrollToRef="pillar-3" linkText="Pillar 3: Impact" />
                </DropdownMenu>
              </UncontrolledDropdown>
              <StickyNavLink scrollToRef="bonuses" linkText="Bonuses" />
              <StickyNavLink scrollToRef="pbcc-faq" linkText="FAQ’s" />
              <UncontrolledDropdown className="dropdown-connect" nav inNavbar>
                <DropdownToggle className="pbcc-nav-list-item pbcc-nav-list-item-connect" nav caret>
                  Connect
                </DropdownToggle>
                <DropdownMenu right>
                  <NavItem>
                    <NavLink className="pbcc-nav-list-item" href="/calls">
                      Book a Clarity Call
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink className="pbcc-nav-list-item" href="mailto:certificate@foodrevolution.org">
                      Email Us
                    </NavLink>
                  </NavItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </Nav>
          </Collapse>
        </Navbar>
      </Container>
    </div>
  );
}

function ClarityCall({id, bgColor = 'black', ...props}) {
  return (
    <SectionCL id={id} color={bgColor} className="text-center text-white">
      <Container>
        <Row>
          <Col>
            <h3 className="section-heading mb-3">Have questions?</h3>
            <p>Chat with a program advisor before enrolling (at no charge!).</p>
            <Button color="cta" className="mb-0" href="/calls/" target="_blank">
              Book A Clarity Call!
            </Button>
          </Col>
        </Row>
      </Container>
    </SectionCL>
  );
}

function ProgramScheduleButton({className = undefined}) {
  const [isOpen, setIsOpen] = useState(false);
  const onClick = useCallback(() => setIsOpen(true), []);
  const toggle = useCallback(() => setIsOpen((isOpen) => !isOpen), []);
  return (
    <>
      <Button color="purple" className="text-16 rounded-top-none" onClick={onClick} block>
        <FontAwesomeIcon icon={faExpand} className="mr-2" />
        View the 2026 Program Schedule
      </Button>
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <Container className="text-center px-sm-4 pt-0 pb-5" fluid>
          <Row>
            <Col>
              <img
                src="https://cdn.foodrevolution.org/pbcc/pbcc-program-snapshot-2026.png"
                alt="2026 program schedule graphic"
                width=""
                height=""
              />
              <Button
                href="https://s3.us-west-2.amazonaws.com/cdn.foodrevolution.org/pbcc/pbcc-program-snapshot-2026.png"
                target="_blank"
                color="purple"
                className="text-16 mt-4"
              >
                <FontAwesomeIcon className="mr-2" icon={faCloudArrowDown} />
                Download Program Schedule
              </Button>
            </Col>
          </Row>
        </Container>
      </Modal>
    </>
  );
}

export default function SalesCopy({variant = 'default', version = 'sales', ctaOverride = null, timerDate, ...props}) {
  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before'
      },
      webinar: webinarSchedule = {
        phase: 'before'
      }
    }
  } = usePhaseState();

  const masterclassOverride = version === 'masterclass';
  const afterMasterclass = webinarSchedule.phase === 'after';

  const phase = defaultSchedule.phase;
  const phaseIteration = defaultSchedule.phaseIteration;
  const beforeRegistrationOpen = phase === 'beforeRegistration';
  const registrationClosed = phase === 'after';
  const clarityCall = defaultSchedule.clarityCall;
  const ctaVisible = defaultSchedule.ctaVisible || masterclassOverride;

  const ctaBonus =
    !beforeRegistrationOpen ||
    phaseIteration === 'insiderPromo' ||
    phaseIteration === 'workshop' ||
    phaseIteration === 'retail' ||
    phaseIteration === 'webinar' ||
    !registrationClosed;
  const wlcBonus = defaultSchedule.wlcBonus || masterclassOverride;
  const mastermindBonus = defaultSchedule.mastermindBonus || masterclassOverride;
  const frsBonus = defaultSchedule.frsBonus || masterclassOverride;

  const frsSpecialBonus =
    (phaseIteration === 'insiderPromo' && !masterclassOverride) ||
    (phaseIteration === 'workshop' && !masterclassOverride);

  const bonusesValue = masterclassOverride
    ? '$12,800'
    : registrationClosed
    ? '$4,436'
    : (() => {
        switch (phaseIteration) {
          case 'insiderPromo':
            return '$13,700';
          case 'workshop':
          case 'webinar':
          case 'retail':
            return '$12,800';
          default:
            return '$4,436';
        }
      })();

  const productId = 'pbcc';
  const productSku = 'pbcc';
  const productName = 'Plant-Based Coaching Certificate';
  const productSlug = masterclassOverride
    ? 'pbcc-2026-deposit-m'
    : phaseIteration === 'insiderPromo'
    ? 'pbcc-2026-deposit-insider'
    : 'pbcc-2026-deposit';
  const productValue = 100;
  const waitListFormUrl = 'https://forms.gle/Ph8UT3auQA4j3EGFA';

  useEffect(() => {
    track('Product Viewed', {
      product_id: productId,
      sku: productSku,
      name: productName,
      variant: productSlug,
      value: productValue,
      currency: 'usd'
    });
  });

  const programStart = 'January 26, 2026';
  const programStartNoYear = 'January 26';
  const programYear = '2026';
  const welcomeCallDate = 'January 13, 2026';

  const appDeadline =
    phaseIteration === 'insiderPromo' && !masterclassOverride
      ? 'August 29, 2025'
      : phaseIteration === 'finalChance'
      ? 'January 25, 2026'
      : 'January 23, 2026';

  const tuitionDue = phaseIteration === 'insiderPromo' && !masterclassOverride ? 'August 29, 2025' : 'January 27, 2026';

  const insiderPromoMonthlyPayment = '$875';
  const insiderPromoMonthlyTotal = '$5,250';
  const insiderPromoFullTotal = '$4,797';
  const tuitionMonthlyPaymentDiscount = '$997';
  const tuitionMonthlyTotalDiscount = '$5,982';
  const tuitionFullPaymentDiscount = '$4,997';
  const tuitionMonthlyPayment = '$1,097';
  const tuitionMonthlyTotal = '$6,582';
  const tuitionFullPayment = '$5,997';

  const tuitionDiscount =
    masterclassOverride && afterMasterclass
      ? false
      : {
          insiderPromo: true,
          workshop: true,
          retail: false,
          webinar: true,
          lateApp: false,
          finalChance: true
        }[phaseIteration];

  const tuitionMonthly =
    masterclassOverride && !afterMasterclass
      ? tuitionMonthlyPaymentDiscount
      : masterclassOverride && afterMasterclass
      ? tuitionMonthlyPayment
      : {
          insiderPromo: insiderPromoMonthlyPayment,
          workshop: tuitionMonthlyPaymentDiscount,
          webinar: tuitionMonthlyPaymentDiscount,
          retail: tuitionMonthlyPayment,
          lateApp: tuitionMonthlyPayment,
          finalChance: tuitionMonthlyPaymentDiscount
        }[phaseIteration];

  const tuitionMonthlyFull =
    masterclassOverride && !afterMasterclass
      ? tuitionMonthlyTotalDiscount
      : masterclassOverride && afterMasterclass
      ? tuitionMonthlyTotal
      : {
          insiderPromo: insiderPromoMonthlyTotal,
          workshop: tuitionMonthlyTotalDiscount,
          webinar: tuitionMonthlyTotalDiscount,
          retail: tuitionMonthlyTotal,
          lateApp: tuitionMonthlyTotal,
          finalChance: tuitionMonthlyTotalDiscount
        }[phaseIteration];

  const tuitionFull =
    masterclassOverride && !afterMasterclass
      ? tuitionFullPaymentDiscount
      : masterclassOverride && afterMasterclass
      ? tuitionFullPayment
      : {
          insiderPromo: insiderPromoFullTotal,
          workshop: tuitionFullPaymentDiscount,
          webinar: tuitionFullPaymentDiscount,
          retail: tuitionFullPayment,
          lateApp: tuitionFullPayment,
          finalChance: tuitionFullPaymentDiscount
        }[phaseIteration];

  const currentTime = new Date().getTime();
  const noTimer = phaseIteration === 'insiderPromo';

  function ApplyNowCta({className = undefined}) {
    return (
      <div className={classNames(`apply-now`, className)}>
        {beforeRegistrationOpen ? (
          <>
            <h4 className="section-heading text-uppercase mb-3">Important Note:</h4>
            <p>We’re still getting the registration process worked out.</p>
            <p>
              To enter your name and email so we can make sure to let you know when registration opens,{' '}
              <a className={classNames(`text-underline`, `link-purple`)} href={waitListFormUrl}>
                click here
              </a>
              .
            </p>
            <h5 className="m-0">Thanks!</h5>
          </>
        ) : registrationClosed ? (
          <>
            <p>
              <b>The deadline to apply for the Coaching Program has passed.</b>
            </p>
            <p>
              If you’ve already submitted a deposit, you have until {tuitionDue} to submit payment for inclusion in the
              program.
            </p>
            <p>
              <a className={classNames(`text-underline`, `link-purple`)} href={waitListFormUrl} target="_blank">
                Click here to be notified if we offer this program again in the future.
              </a>
            </p>
          </>
        ) : (
          <>
            {masterclassOverride ? (
              <>
                {!afterMasterclass && (
                  <h4 className="section-heading text-uppercase mb-3">Special Offer Available Now</h4>
                )}
                {currentTime <= timerDate && <CountdownTimer time={timerDate} className="countdown-dark" />}
              </>
            ) : (
              <>
                {phaseIteration === 'lateApp' || phaseIteration === 'finalChance' ? (
                  <p className="mb-4">
                    The deadline to apply for the Coaching Program has passed. But you’re in luck. We’re extending the
                    timeline for latecomers!
                  </p>
                ) : null}
                {!noTimer && (
                  <>
                    <h5 className="section-heading mb-2">
                      {
                        {
                          workshop: 'Limited Time Bonuses Expiring in:',
                          retail: 'Limited Time Bonuses Expiring in:',
                          webinar: 'Tuition Discount & Limited Time Bonus Expiring in:',
                          lateApp: <>Application Deadline: {appDeadline}</>,
                          finalChance: <>Application Deadline: {appDeadline}</>
                        }[phaseIteration]
                      }
                    </h5>
                    <CountdownTimer time={timerDate} className="countdown-dark" />
                  </>
                )}
                {phaseIteration === 'insiderPromo' && (
                  <>
                    <h4 className="section-heading mb-5">The Next Cohort Begins in January, {programYear}.</h4>
                    <h3 className="mt-4">Apply now to get the FRN Insider price* and limited-time bonuses</h3>
                  </>
                )}
              </>
            )}
            {ctaBonus && (
              <>
                {masterclassOverride ? (
                  <>
                    {afterMasterclass ? (
                      <h5 className="mt-4 mb-4">Apply now to receive these limited-time bonuses:</h5>
                    ) : (
                      <>
                        <h5 className="mt-4">Up to $1,000 OFF</h5>
                        <h5 className="mt-0 mb-4">Plus 3 limited-time bonuses:</h5>
                      </>
                    )}
                  </>
                ) : (
                  <h5 className="mt-4 mb-4">
                    {
                      {
                        workshop: 'Apply now to get up to $1,000 OFF AND receive these limited-time bonuses:',
                        retail: 'Apply now to receive these limited-time bonuses:',
                        webinar: 'Apply now to get up to $1,000 OFF AND receive these limited-time bonuses:'
                      }[phaseIteration]
                    }
                  </h5>
                )}
                <div className="d-flex justify-content-center mb-0">
                  <ListIcon className="text-left m-0">
                    {(masterclassOverride && !afterMasterclass) ||
                    phaseIteration === 'insiderPromo' ||
                    phaseIteration === 'workshop' ||
                    phaseIteration === 'webinar' ? (
                      <>
                        Tuition Discount:{' '}
                        <del>
                          {tuitionMonthlyTotal} ({tuitionMonthlyPayment}/month for 6 months)
                        </del>
                        <br className="d-none d-xl-block" />{' '}
                        <span className="text-green">
                          {tuitionMonthlyFull} (6 monthly payments of {tuitionMonthly})
                        </span>{' '}
                        or <del>{tuitionFullPayment}</del> <span className="text-green">{tuitionFull}</span>
                      </>
                    ) : null}

                    {phaseIteration === 'insiderPromo' && !masterclassOverride ? (
                      <>2021–2026 Food Revolution Summit Empowerment Packages (Value $1,182)</>
                    ) : null}

                    {(phaseIteration === 'retail' && !masterclassOverride) ||
                    (phaseIteration === 'webinar' && !masterclassOverride) ||
                    (phaseIteration === 'workshop' && !masterclassOverride) ? (
                      <>2025–2026 Food Revolution Summit Empowerment Packages (Value $394)</>
                    ) : null}

                    {masterclassOverride ||
                    phaseIteration === 'insiderPromo' ||
                    phaseIteration === 'workshop' ||
                    phaseIteration === 'retail' ||
                    phaseIteration === 'webinar' ? (
                      <>{wlcBonus && <>Unlimited Membership in WHOLE Life Club (Value $497/year)</>}</>
                    ) : null}

                    {masterclassOverride || phaseIteration === 'retail' || phaseIteration === 'webinar' ? (
                      <>{mastermindBonus && <>Ocean Robbins’ Food Revolution Mastermind (Value $3,000)</>}</>
                    ) : null}
                  </ListIcon>
                </div>
                {phaseIteration === 'lateApp' ? (
                  <>
                    <h5 className="mt-0 mb-5">
                      Secure your spot by {appDeadline}, to sneak in under the wire! (You’ll get recordings of the
                      Welcome Calls so you won’t miss a thing.)
                    </h5>
                  </>
                ) : phaseIteration === 'finalChance' ? (
                  <>
                    <h5 className="mt-0 mb-5">
                      Secure your spot by {tuitionDue} to sneak in under the wire! (You’ll still get all the recordings,
                      so you won’t miss a thing.)
                    </h5>
                  </>
                ) : (
                  <p className="mb-5">
                    <b>
                      <i>And more — see the complete list of more than $10,000 worth of bonuses below!</i>
                    </b>
                  </p>
                )}
              </>
            )}
            <Row className={classNames(`align-items-stretch`)}>
              <Col lg="6" className="d-flex flex-column mb-4 mb-lg-0">
                <div className="box-shadow-black-50 rounded flex-grow-1 d-flex flex-column">
                  <div className="background-black text-white p-3 rounded-top">
                    <h5 className="m-0">Monthly payments</h5>
                  </div>
                  <div className="p-3 my-auto">
                    {tuitionDiscount || (masterclassOverride && !afterMasterclass) ? (
                      <p className="mb-2">
                        <del>
                          Regular price: 6 payments of {tuitionMonthlyPayment} ({tuitionMonthlyTotal} total)
                        </del>
                      </p>
                    ) : null}
                    <p className="text-green m-0">
                      <b>
                        6 payments of {tuitionMonthly} ({tuitionMonthlyFull} total)
                      </b>
                    </p>
                  </div>
                </div>
              </Col>
              <Col lg="6" className="d-flex flex-column">
                <div className="box-shadow-black-50 rounded flex-grow-1 d-flex flex-column">
                  <div className="background-black text-white p-3 rounded-top">
                    <h5 className="m-0">Pay in full and save</h5>
                  </div>
                  <div className="p-3 my-auto">
                    {tuitionDiscount || (masterclassOverride && !afterMasterclass) ? (
                      <p className="mb-2">
                        <del>Regular price: 1 payment of {tuitionFullPayment}</del>
                      </p>
                    ) : null}
                    <p className="text-green m-0">
                      <b>1 payment of {tuitionFull}</b>
                    </p>
                  </div>
                </div>
              </Col>
            </Row>

            {phaseIteration === 'insiderPromo' && (
              <>
                <h5>Special FRN Insider Promotion</h5>
                <ol className="text-left">
                  <li>
                    <p className="mb-1">
                      <b>Submit a $100 fully refundable deposit</b>
                    </p>
                    <p>
                      This holds your spot and guarantees access to the Insider pricing when enrollment opens in August.
                      Cancel anytime for a full refund.
                    </p>
                  </li>
                  <li>
                    <p className="mb-1">
                      <b>Fill out an application</b>
                    </p>
                    <p>
                      After you make your deposit, you’ll receive a follow-up email prompting you to complete an
                      application. We’ll begin reviewing applications in early August and notify you of your status by
                      August 14. If you’re not accepted, we’ll refund your deposit in full.
                    </p>
                  </li>
                  <li>
                    <p className="mb-1">
                      <b>Make your discounted tuition payment by Friday, August 29, 2025.</b>
                    </p>
                    <p>You’ll have the choice to pay in full — or sign up for a 6-month payment plan.</p>
                  </li>
                </ol>
              </>
            )}
            <CheckoutButton sku={productSku} price={productValue} slug={productSlug} className={classNames(`my-4`)}>
              {phaseIteration === 'insiderPromo' ? 'Submit Deposit Now' : 'Apply Now'}
            </CheckoutButton>

            {!registrationClosed && (
              <>
                {phaseIteration === 'lateApp' || phaseIteration === 'finalChance' ? (
                  <>
                    <h4 className="mt-4 mb-3">By Popular Demand: Deadline Extended!</h4>
                    <p>
                      <b>FINAL Application Deadline: {appDeadline}</b>
                    </p>
                    <p>
                      <b>Program Start Date: {programStart}</b>
                    </p>
                    <p>
                      When you submit your $100 refundable deposit, you will receive your application, which takes about
                      10 minutes to complete. But you do have to ask fast. Don’t delay — join now!
                    </p>
                    <p>
                      <b>
                        <i>
                          Pay by {tuitionDue} to squeeze in at the last minute — with recordings of welcome calls so you
                          won’t miss a thing!
                        </i>
                      </b>
                    </p>
                  </>
                ) : (
                  <>
                    <h4 className="text-uppercase mt-4 mb-3">Important Dates</h4>
                    {phaseIteration === 'insiderPromo' && !masterclassOverride ? (
                      <p>
                        <b>First Payment Due</b>
                      </p>
                    ) : null}
                    {phaseIteration !== 'insiderPromo' && (
                      <p>
                        <b>Welcome Call: {welcomeCallDate}</b>
                      </p>
                    )}
                    <p>
                      <b>Program Start Date: {programStart}</b>
                    </p>
                    {phaseIteration === 'insiderPromo' && !masterclassOverride ? (
                      <p>
                        This will take you to a page to submit your $100 refundable deposit, and then you can apply. We
                        will be in touch by August 14 about your admission status.
                      </p>
                    ) : (
                      <>
                        <p>
                          When you submit your $100 refundable deposit, you will receive{' '}
                          <br className="d-none d-xl-block" />
                          your application, which takes about 10 minutes to complete.
                        </p>
                        <p>
                          Your deposit counts towards your total tuition. If your application is accepted,{' '}
                          <br className="d-none d-xl-block" />
                          your space will be held for two weeks so you can submit payment.
                        </p>
                      </>
                    )}
                    <p>
                      <b>
                        <i>
                          Limited spots are available. Submit your deposit <br className="d-none d-xl-block" />
                          and secure your place in the next cohort now.
                        </i>
                      </b>
                    </p>
                  </>
                )}
                {phaseIteration === 'insiderPromo' && !masterclassOverride ? (
                  <>
                    <p>
                      Your deposit counts towards your total tuition. If your application is accepted, your space will
                      be held at the FRN Insider price until {tuitionDue}.
                    </p>
                    <p>
                      <b>
                        If you do not enroll in the program by {tuitionDue}, we will refund your deposit by
                        mid-September.
                      </b>
                    </p>
                  </>
                ) : null}
                {clarityCall && (
                  <p>
                    <a className={classNames(`text-underline`)} href="/calls/" target="_blank">
                      Or click here to speak with a program advisor.
                    </a>
                  </p>
                )}
              </>
            )}
            <SalesDisclaimer />
          </>
        )}
      </div>
    );
  }
  return (
    <>
      {version === 'sales' && (
        <>
          <div className="sticky-header">
            <div className="pbcc-banner background-white py-2">
              <Container>
                <Row className="align-items-center text-center">
                  <Col xs="6" md="4" lg="3" className="">
                    <img
                      className="img-course-logo"
                      src="https://cdn.foodrevolution.org/pbcc/pbcc-certificate-logo-color.svg"
                      alt="plant-based coaching certificate logo"
                      style={{maxHeight: '50px'}}
                    />
                  </Col>
                  <Col xs="6" md="5" xl="6" className="my-2 my-md-0">
                    <div className="pbcc-banner-text text-green text-800">
                      {masterclassOverride ? (
                        <>
                          Save up to $1,000+ <br className="d-none d-sm-block d-lg-none" />
                          when you enroll right now.
                        </>
                      ) : (
                        <>
                          {(() => {
                            switch (phaseIteration) {
                              case 'insiderPromo':
                                return (
                                  <>
                                    Save over $1,000+ <br className="d-none d-sm-block d-lg-none" />
                                    when you enroll right now.
                                  </>
                                );
                              case 'workshop':
                              case 'webinar':
                                return (
                                  <>
                                    Save up to $1,000+ <br className="d-none d-sm-block d-lg-none" />
                                    when you enroll right now.
                                  </>
                                );
                              case 'retail':
                                return <>Apply now to receive limited-time bonuses.</>;
                              case 'lateApp':
                              case 'finalChance':
                                return <>Application Deadline: {appDeadline}</>;
                              default:
                                return null;
                            }
                          })()}
                        </>
                      )}
                    </div>
                  </Col>
                  <Col xs="12" md="3" xl="2">
                    <Button
                      className="text-16"
                      color="cta"
                      spy={true}
                      offset={-75}
                      to="pbcc-cta-1"
                      smooth={true}
                      href="#"
                      tag={Link}
                      block
                    >
                      I’m Ready
                    </Button>
                  </Col>
                </Row>
              </Container>
            </div>
            <SalesNavigation className="background-light-gray" />
          </div>
          <SectionCL id="pbcc-header-cta" color="black" className="text-white" order="first">
            <Container>
              <Row className="align-items-center">
                <Col lg="6" className="text-center text-lg-left">
                  <h5 className="section-heading">
                    Are you ready to become a LEADER <br className="d-none d-xl-block" />
                    in the food revolution movement?
                  </h5>
                  <h1 className="text-h2 mt-0 mb-3">
                    Get certified as a <br className="d-none d-xl-block" />
                    Plant-Based Coach.
                  </h1>
                  <h5 className="m-0">
                    Enrollment is open once per year <br className="d-none d-xl-block" />
                    and is limited to only 300 participants.
                  </h5>
                </Col>
                <Col lg="6" className="d-none d-lg-block">
                  <Video
                    url="https://www.youtube.com/watch?v=C1QszkYlWJI"
                    thumbnail="https://cdn.foodrevolution.org/pbcc/video-thumb/pbcc-sales-dynamic-thumbnail-20230927.gif"
                    noButton
                  />
                </Col>
              </Row>
            </Container>
          </SectionCL>
        </>
      )}
      {version === 'masterclass' && (
        <>
          {ctaVisible && (
            <SectionCL id="pbccm-cta-1" color="purple">
              <Container>
                <Row>
                  <Col>
                    <div className="background-white rounded box-shadow-black-50 p-4 p-lg-5 text-center">
                      <ApplyNowCta />
                    </div>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
          )}
          {clarityCall && <ClarityCall id="pbccm-clarity-1" />}
        </>
      )}
      <SectionCL id="pbcc-leader" color="white">
        <Container>
          <Row>
            <Col>
              <div className="text-center">
                <p>
                  <i>Accreditation or CE units available from:</i>
                </p>
                <div className="d-flex align-items-center justify-content-center">
                  <div>
                    <img
                      src="https://cdn.foodrevolution.org/pbcc/images/pbcc-ccr-mark-20250716.png"
                      style={{maxHeight: '160px'}}
                    />
                  </div>
                  <div>
                    <img src="https://cdn.foodrevolution.org/pbcc/lmuf-logos.png" style={{maxHeight: '160px'}} />
                  </div>
                </div>
              </div>
              <h3 className="text-center">
                {
                  {
                    default: <>Why Invest in a Plant-Based Coaching Certificate?</>,
                    ad: <>Find Fulfillment and Success</>
                  }[variant]
                }
              </h3>
              {
                {
                  default: (
                    <>
                      <p>
                        <b>Find Fulfillment and Success.</b>
                      </p>
                      <p>
                        Did you know Holistic Health and Wellness is one of the most profitable coaching niches for
                        2025? And did you also know that the more you niche down your coaching offer… the easier it is
                        to find high-paying clients?
                      </p>
                      <p>
                        Those are just two reasons to consider making this the year you finally step into your power as
                        a plant-based coach.
                      </p>
                      <p>
                        Whether you’re already an experienced coach or you’re just starting to help others transform
                        their lives, the right coaching certificate opens the door to endless possibilities.
                      </p>
                      <p>
                        <b>For new or aspiring coaches,</b> this is your chance to get the training you need to launch a
                        rewarding new career. You can finally make a real impact through your passion for health and
                        well-being instead of just dreaming about creating positive change.{' '}
                        <b>For experienced coaches or health professionals,</b> embracing FRN’s plant-based coaching
                        approach is a powerful way to focus and clarify your offerings or add a powerful new offering to
                        your existing clients. <b>Regardless of your background or your plans,</b> becoming a
                        plant-based coach empowers you to foster transformation, find personal fulfillment, and make a
                        meaningful difference in the lives of others.
                      </p>
                      <p>
                        Now’s your chance to join us! This premier coaching certificate program combines in-depth
                        plant-based nutrition education, results-driven coaching skills, and practical training to help
                        you create greater impact and income — while making a meaningful difference in the world.
                      </p>
                    </>
                  ),
                  ad: (
                    <>
                      <p>
                        Holistic Health and Wellness is one of the most profitable coaching niches for 2025. And niching
                        down coaching services is a proven method for finding high-paying clients.
                      </p>
                      <p>That’s why 2025 is the perfect year to step into your power as a plant-based health coach.</p>
                      <p>
                        Whether you’re already an experienced coach or you’re just starting to help others transform
                        their lives, the right coaching certificate opens the doors to endless possibilities.{' '}
                        <b>New or aspiring coaches</b> get the training they need to launch a rewarding new career — so
                        they can finally make a real impact instead of just dreaming about creating positive change.{' '}
                        <b>Experienced coaches or health professionals</b> get a powerful way to niche down even
                        further, and add a transformative new offering to existing clients.
                      </p>
                      <p>
                        Becoming a plant-based coach empowers transformation, increases personal fulfillment, and makes
                        a meaningful difference in the lives of others. Join us for in-depth plant-based nutrition
                        education, results-driven coaching skills, and practical training that helps create greater
                        impact and income — while making a meaningful difference in the world.
                      </p>
                      <p>Join us!</p>
                    </>
                  )
                }[variant]
              }
            </Col>
          </Row>
        </Container>
      </SectionCL>
      {version === 'sales' && (
        <>
          {ctaVisible && (
            <SectionCL id="pbcc-cta-1" color="purple">
              <Container>
                <Row>
                  <Col>
                    <div className="background-white rounded box-shadow-black-50 p-4 p-lg-5 text-center">
                      <ApplyNowCta />
                    </div>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
          )}
          {clarityCall && <ClarityCall id="pbcc-clarity-1" />}
        </>
      )}
      <SectionCL id="pbcc-spotlight" color="light-gray" className="text-center">
        <Container>
          <Row>
            <Col>
              <h3 className="section-heading mb-3">Curious to hear what our students have to say about PBCC?</h3>
              <p className="text-h5">Plant-Based Coach Spotlights: Their stories in their own words.</p>
              <p className="text-small">
                These testimonials reflect the experiences of our program’s graduates. Each of them is unique. And so
                are you! <br className="d-none d-xl-block" />
                Your experiences will vary, of course. And no, we never offer compensation for these statements!
              </p>
            </Col>
          </Row>
          <Row className="align-items-stretch mt-4">
            {studentSpotlight.map((item, key) => (
              <Col xs="12" lg="6" className="d-flex flex-column mb-5 px-3" key={key}>
                <div className="background-white d-flex flex-column flex-grow-1 box-shadow-black-50 rounded-top">
                  <div className="my-auto p-3">
                    <p className="mb-2">
                      <b>{item.name}</b>
                    </p>
                    <p className="mb-0">{item.title}</p>
                  </div>
                  <div className="spotlight-video">
                    <Video url={item.videoUrl} label={`pbcc student spotlight ${item.name}`} />
                  </div>
                </div>
              </Col>
            ))}
          </Row>
          <Row className="text-left mt-4">
            <Col>
              {['pbccBrendaD', 'pbccTerriB', 'pbccWendyM', 'pbccTraceyT', 'pbccTeriT', 'pbccTeklaG'].map((key, i) => (
                <TestimonialBubble id={key} key={key} bgColor="white" className={classNames(i === 5 ? '' : 'mb-5')} />
              ))}
            </Col>
          </Row>
        </Container>
      </SectionCL>
      <div id="why">
        {version === 'sales' && (
          <>
            <SectionCL
              id="pbcc-needs"
              color="white"
              imgPosition="right"
              imgDisplayStyle="rectangle"
              imgBgAlignmentDesktop="right center"
              imgUrl="https://cdn.foodrevolution.org/pbcc/images/2021300421-rect.jpg"
              angleBottomDisplay="lg-none"
            >
              <h2 className="section-heading text-uppercase">The World Needs You</h2>
              <p>
                Despite astonishing advances in modern medicine, people are sicker than ever. Rates of heart disease,
                obesity, type 2 diabetes, autoimmune conditions, osteoporosis, Alzheimer’s, and other chronic diseases
                are spiraling out of control.
              </p>
              <p>
                In the U.S. alone, we now spend more than $4 trillion a year on medical care — and up to 80% of that is
                spent on chronic conditions. Most of these diseases are largely preventable — and often reversible —
                through lifestyle change.<sup>1</sup>
              </p>
              <p>
                At the same time, interest in <i>healthy eating</i> is booming.
              </p>
              <p>But many people are being led astray.</p>
              <p>
                Fad diets and viral trends — keto, carnivore, low-carb, low-lectin — are gaining popularity. And while
                some folks feel better when they ditch ultra-processed foods (no surprise there!), these diets often
                ignore the best available science and fail to provide long-term solutions. People end up discouraged,
                burned out, and back where they started — or worse.
              </p>
              <p>The truth is:</p>
              <p>
                Decades of research show that a <b>whole-food, plant-forward</b> diet is one of the most powerful ways
                to prevent disease, support a healthy weight, and live a vibrant life.
              </p>
              <p>It’s also far more sustainable for our planet.</p>
            </SectionCL>
            <SectionCL
              id="pbcc-wait"
              color="white"
              imgPosition="left"
              imgDisplayStyle="rectangle"
              imgUrl="https://cdn.foodrevolution.org/pbcc/images/1474980786-rect.jpg"
              innerClass="sectionCL-padding-lg"
            >
              <h2 className="section-heading">This can’t wait.</h2>
              {
                {
                  default: (
                    <>
                      <p>
                        Animal agriculture has become one of the most ecologically devastating industries on Earth —
                        driving deforestation, water pollution, species extinction, and climate chaos. Just raising
                        livestock for food emits more greenhouse gases than all the world’s transportation combined. And
                        it inflicts massive suffering on billions of animals.<sup>2</sup>
                      </p>
                      <p>Shifting towards more plant-based foods isn’t just a personal health choice.</p>
                      <p>It’s a moral and ecological imperative.</p>
                      <p>
                        And yet… while tens of millions of people want to eat healthfully, ethically and sustainably —
                        most don’t know what that actually means.{' '}
                        <b>
                          The survival of millions of people, and the future our children will inherit, is in our hands
                          (and on our forks!). There’s no time to waste.
                        </b>
                      </p>
                    </>
                  ),
                  ad: (
                    <>
                      <p>
                        Animal agriculture is one of the most environmentally destructive practices on Earth — fueling
                        deforestation, air and water pollution, topsoil erosion, and environmental chaos. The single act
                        of raising livestock for food is responsible for more greenhouse gas emissions than the sum
                        total of all transportation emissions.<sup>2</sup>
                      </p>
                      <p>
                        Replacing animal products with plant-based food is critical to the sustainability of ecosystems
                        and the well-being of future generations.
                      </p>
                      <p>
                        <b>There’s no time to waste.</b>
                      </p>
                    </>
                  )
                }[variant]
              }
            </SectionCL>
          </>
        )}
        <SectionCL
          id="pbcc-change"
          color="light-gray"
          imgPosition="right"
          imgDisplayStyle="rectangle"
          imgUrl="https://cdn.foodrevolution.org/pbcc/images/1357522251-square.jpg"
          angleBottomDisplay="lg-none"
        >
          <h3 className="section-heading">More people than ever are motivated to make a change.</h3>
          <ListIcon>
            <>They want to thrive — but they’re overwhelmed by conflicting opinions.</>
            <>
              They want to eat for a better world — but they don’t know what’s actually helpful, and what’s just a bunch
              of greenwashing
            </>
            <>They want to make real change — but they lack the tools, confidence, or support to follow through.</>
          </ListIcon>
          <p>A skilled plant-based coach can make all the difference.</p>
          <p>
            People need guidance. They need community. They need someone they can trust to help them make lasting,
            meaningful change.
          </p>
          <p className="text-h5 text-800">That’s where the right coach comes in. That’s where YOU come in.</p>
        </SectionCL>
        <SectionCL
          id="pbcc-connection"
          color="light-gray"
          imgPosition="left"
          imgDisplayStyle="rectangle"
          imgBgAlignmentDesktop="right center"
          imgUrl="https://cdn.foodrevolution.org/pbcc/images/1356197695-rect.jpg"
          innerClass="sectionCL-padding-lg"
        >
          <h3 className="section-heading">In an AI-driven world, human connection is more important than ever.</h3>
          <p>
            Technology is changing everything. AI can now write essays, analyze lab results, and generate meal plans in
            seconds. But it still can’t do what you, a real human being, can do.
          </p>
          <p>It can’t look someone in the eyes — even virtually — and truly see them.</p>
          <p>It can’t feel their struggles or celebrate their wins.</p>
          <p>It can’t hold them accountable with empathy, curiosity, and compassion.</p>
          <p>
            And it can’t offer the emotional presence and nuanced support that creates real, lasting transformation.
          </p>
          <p>Learning how to eat better is one thing.</p>
          <p>Staying on track when life gets messy is another.</p>
          <p>That’s why coaching — real human-to-human coaching — is so powerful.</p>
          <p>We live in a world where people are overwhelmed by information and starving for connection.</p>
          <p>
            If you can help someone feel seen, supported, and capable of real change — you’ll be giving them a gift no
            app or AI can replace.
          </p>
          <p>And you’ll be building a future where health, compassion, and wisdom still have a human face.</p>
        </SectionCL>
        <SectionCL id="pbcc-joinus" color="black" className="text-center text-white">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading">
                  <i>
                    {
                      {
                        default: <>Help people live longer, happier, and healthier lives</>,
                        ad: <>Help people live long, happy, and healthy lives</>
                      }[variant]
                    }
                  </i>
                </h3>
                <Button color="cta" size="lg" spy={true} offset={-75} to="pbcc-cta-1" smooth={true} href="#" tag={Link}>
                  Join Us
                </Button>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="pbcc-help-impact" color="white">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading text-center">Do you want to help change the way the world eats?</h3>
                <p className="text-center">
                  We urgently need leaders, experts, and coaches to bring this food-based{' '}
                  <br className="d-none d-xl-block" />
                  movement to more people in a deeper, more personal way.
                </p>
                <h4 className="mt-0 text-center mb-5">
                  We designed the world’s most comprehensive plant-based coaching program that combines all three of
                  these critical pillars.
                </h4>
                <ImgCard //@ts-ignore
                  img={require('static/backgrounds/854725372-wide.jpg')}
                  className="imgBulletCard-300 mb-5"
                >
                  <h5 className="section-heading">Nutrition: Science-Backed, Inclusive, and Practical</h5>
                  <p className="mb-0">
                    Unlike many other certifications, we focus exclusively on whole food, plant-based nutrition and its
                    powerful impact on health. Our curriculum goes beyond the basics. You’ll learn to separate facts
                    from fiction, avoid common plant-based pitfalls, and build a plant-strong kitchen with confidence.
                    Through our inclusive “Big Tent” approach, we celebrate progress over perfection and teach coaches
                    to meet people where they are, honoring every step toward more plants.
                  </p>
                </ImgCard>
                <ImgCard
                  img="https://cdn.foodrevolution.org/pbcc/images/1218076467.jpg"
                  className="imgBulletCard-300 mb-5"
                >
                  <h5 className="section-heading">Coaching: Structure, Skill, and Heart</h5>
                  <p className="mb-0">
                    Our certificate combines a solid evidence-based coaching framework with Food Revolution Network’s
                    unique approach to personal transformation. You’ll build core coaching competencies and learn how to
                    create a safe, empowering container for growth, while adding FRN’s signature of compassion, active
                    listening, and values-driven communication. The result? A coaching style that fosters real
                    breakthroughs and lasting change.
                  </p>
                </ImgCard>
                <ImgCard
                  img="https://cdn.foodrevolution.org/pbcc/images/1411238326.jpg"
                  className="imgBulletCard-300 mb-5"
                >
                  <h5 className="section-heading">Impact: Purpose-Driven and Results-Oriented</h5>
                  <p className="mb-0">
                    More than a credential, the Plant-Based Coaching Certificate is a launchpad for change-makers. As
                    part of Food Revolution Network, the global leader in the plant-based movement, you’ll gain access
                    to real-world strategies to make a difference and the ongoing support of faculty and like-hearted
                    peers. Whether you dream of starting your own practice, joining a clinic, or igniting change in your
                    community, this will help you define your unique path and equip you to create ripple effects of
                    healing and hope.
                  </p>
                </ImgCard>
                <h5 className="text-center text-400">
                  Food Revolution Network’s Plant-Based Coaching Certificate helps today’s brightest leaders step into
                  their power as agents for change.
                </h5>
                <h3 className="text-center mb-0">3 Pillars. Once Exceptional Program.</h3>
                <img
                  src="https://cdn.foodrevolution.org/pbcc/images/pillars-20250708.png"
                  alt="pillars graphic"
                  width="1200"
                  height="595"
                />
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="pbcc-imagine" color="light-gray">
          <Container>
            <Row>
              <Col>
                <h4 className="section-heading text-uppercase text-center mb-5">Imagine…</h4>
                <Row className="align-items-stretch justify-content-center">
                  {imagineList.map((item, i) => (
                    <Col xs="12" md="6" xl="4" className="d-flex flex-column mb-4">
                      <div className="d-flex align-items-center background-white box-shadow-black-50 rounded p-4 flex-grow-1">
                        <div className="text-green">
                          <FontAwesomeIcon
                            className="text-h1"
                            icon={require(`@fortawesome/pro-${item.iconSet}-svg-icons`)[item.icon]}
                          />
                        </div>
                        <div className="pl-4">{item.text}</div>
                      </div>
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="pbcc-intro" color="white" className="text-center">
          <Container>
            <Row>
              <Col>
                <p className="mb-2">
                  <i>Introducing</i>
                </p>
                <img
                  className="img-course-logo mb-3"
                  src="https://cdn.foodrevolution.org/pbcc/pbcc-certificate-logo-color.svg"
                  alt="plant-based coaching certificate logo"
                />
                <h4 className="text-400 mt-0">
                  The Premier Coaching Certificate that Combines <br className="d-none d-xl-block" />
                  Plant-Based Nutrition, Coaching, and Impact
                </h4>
                <h3 className="mt-5">Together, We Can Create</h3>
                <div className="d-flex justify-content-center">
                  <ListIcon className="text-left m-0">
                    <>
                      {
                        {
                          default: <>A food revolution movement that changes the status quo</>,
                          ad: <>A movement that stands up to the status quo</>
                        }[variant]
                      }
                    </>
                    <>A world where people who work for good can make a healthy living</>
                    <>
                      A flexible work schedule where you get to inspire meaningful change{' '}
                      <br className="d-none d-xl-block" />
                      for people, families, and communities
                    </>
                    {variant === 'default' && <>A healthier, less polluted planet</>}
                    <>A new standard of ethics that respects all life (including animals!)</>
                    {variant === 'default' && (
                      <>An end to chronic diseases that can be prevented with diet and lifestyle</>
                    )}
                    <>
                      <b>A healthy living, promoting healthy people and a healthy planet</b>
                    </>
                  </ListIcon>
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="pbcc-for-you" color="light-gray">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-center">This Is For You If You Are…</h2>
              </Col>
            </Row>
            <Row>
              <VerticalCard
                img="https://cdn.foodrevolution.org/pbcc/images/1143103422-20231005.jpg"
                heading="Passionate about the power of a whole-food, plant-based lifestyle"
                colLg="4"
              >
                {
                  {
                    default: <>and are ready to spread the word, so you can help people heal their lives</>,
                    ad: <>and ready to spread the word to help people heal their lives</>
                  }[variant]
                }
              </VerticalCard>
              <VerticalCard
                img="https://cdn.foodrevolution.org/pbcc/images/1034036368.jpg"
                heading="An aspiring leader"
                colLg="4"
              >
                {
                  {
                    default: (
                      <>
                        who wants to take the plant-based revolution to your communities — and to effect change on a
                        local and global level
                      </>
                    ),
                    ad: (
                      <>
                        who wants to take the plant-based revolution to different communities — and to effect change on
                        a local and global level
                      </>
                    )
                  }[variant]
                }
              </VerticalCard>
              <VerticalCard
                //@ts-ignore
                img={require('static/backgrounds/1293735269-wide.jpg')}
                heading="Looking to make a difference by doing something that really matters"
                colLg="4"
              >
                {
                  {
                    default: (
                      <>
                        with the potential to make a living helping people create lasting and health-promoting habit
                        change
                      </>
                    ),
                    ad: (
                      <>
                        with the potential to make a living helping people create lasting and health-promoting habit
                        change
                      </>
                    )
                  }[variant]
                }
              </VerticalCard>
            </Row>
            <Row className="text-center mt-4">
              <Col>
                <h5 className="section-heading">
                  And you want to be a positive influence on the <br className="d-none d-xl-block" />
                  future of the planet and all its inhabitants.
                </h5>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="pbcc-transform" color="white">
          <Container>
            <Row>
              <Col>
                <p>
                  Food Revolution Network’s Plant-Based Coaching Certificate is open to an extremely limited number of
                  participants <b>(just 300!)</b> who want to expand their current skills to include plant-based
                  nutrition coaching. Enrollment opens only once a year to ensure a cohesive cohort of students in a
                  small-group learning environment.
                </p>
                <p>
                  {
                    {
                      default: (
                        <>
                          Upon completion, you’ll be able to put your skills to great use in a practically limitless
                          variety of settings, from your own coaching business to wellness centers, spas, holistic
                          healthcare offices, senior centers, schools, restaurants, faith-based communities, at the
                          government level, with your own family and friends, and more.
                        </>
                      ),
                      ad: (
                        <>
                          Upon completion, you’ll be able to put your skills to use in a practically limitless variety
                          of settings, from your own coaching business to wellness centers, spas, holistic healthcare
                          offices, senior centers, schools, restaurants, faith-based communities, with your own family
                          and friends, and more.
                        </>
                      )
                    }[variant]
                  }
                </p>
                {
                  {
                    default: (
                      <>
                        <h3 className="mt-4 mb-3">The first transformation is your own</h3>
                        <p>
                          Whether you’re already plant-based, looking to become plant-based, or simply want to eat more
                          highly nutritious plant-based foods…
                        </p>
                        <p>
                          Plant-Based Coaching Certificate gives you a world-class education in the nutrition foundation
                          that most benefits your own health and well-being.
                        </p>
                        <p>
                          You’ll discover the dietary pattern that’s followed by the world’s longest-living people so
                          you can incorporate the planet’s healthiest food into your own diet — and reap the rewards of
                          more energy, greater joy and vitality, disease prevention, and longevity.
                        </p>
                        <h5 className="mt-0">Gain cutting-edge insights into the science of behavior change</h5>
                        <p>
                          Why do we do what we do? How can we change our habits for lasting results? What you discover
                          during your Plant-Based Coaching Certificate will change your life in profound ways. And it
                          gives you critical skills to be an effective changemaker in the lives of others.
                        </p>
                      </>
                    ),
                    ad: (
                      <>
                        <p>
                          Whether you are already plant-based or simply want to eat more highly nutritious plant-based
                          foods, you’ll gain a world-class education in the nutrition foundation that will benefit your
                          own health and well-being.
                        </p>
                        <p>
                          The program shows you the dietary pattern that’s followed by the world’s longest-living people
                          so you can incorporate the planet’s healthiest food into your own diet — and reap the rewards.
                        </p>
                      </>
                    )
                  }[variant]
                }
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="pbcc-testimonials-2" color="light-gray">
          <Container>
            <Row>
              <Col>
                {['pbccJonJew', 'pbccAnnL', 'pbccTaiSummers', `pbccBonnieN`].map((key, i) => (
                  <TestimonialBubble id={key} key={key} bgColor="white" className={classNames(i === 3 ? '' : 'mb-5')} />
                ))}
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="pbcc-dowork" color="black" className="text-center text-white">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading">Do Work That Makes a Difference</h3>
                <Button color="cta" size="lg" spy={true} offset={-75} to="pbcc-cta-1" smooth={true} href="#" tag={Link}>
                  Join Us
                </Button>
              </Col>
            </Row>
          </Container>
        </SectionCL>
      </div>
      <div id="events">
        <SectionCL id="pbcc-events" color="white" className="">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-center">Upcoming Events</h2>
                <p>
                  What if your passion for healthy living could become a career that transforms lives,{' '}
                  <br className="d-none d-xl-block" />
                  starting with your own?
                </p>
                <p>
                  Imagine feeling aligned in your work, clear in your message, and supported as you make a real impact.
                </p>
                <p>If that’s the path you’re ready for, we’re here to help you take the next step.</p>
                <p>Yes, it’s an investment...in time, in energy, and in your future.</p>
                <p>But the transformation is real, and you won’t be doing it alone.</p>
                <p>Want to talk it through?</p>
                {clarityCall ? (
                  <p>
                    <b>
                      Book a Clarity Call with one of our Program Advisors, or join an upcoming Information Session{' '}
                      <br className="d-none d-xl-block" />
                      for a guided walkthrough of the training with our Program Director, and connect with others{' '}
                      <br className="d-none d-xl-block" />
                      on the journey.
                    </b>
                  </p>
                ) : (
                  <p>
                    <b>
                      Join an upcoming Information Session for a guided walkthrough of the training with our Program
                      Director, and connect with others on the journey.
                    </b>
                  </p>
                )}
                <div className="background-light-gray rounded box-shadow-black-50 mb-5 mt-5">
                  <div className="p-4 p-lg-5">
                    <h3 className="section-heading">Information Sessions</h3>
                    <p>
                      Join a 1-hour live group session to get a{' '}
                      <b>comprehensive overview of the Plant-Based Coaching Certificate Program.</b>
                    </p>
                    <p>
                      Each session includes a concise presentation followed by an open Q&A with members of our PBCC Core
                      Team: Tracy Garrigan (Program Manager), Lauren Weiner (Program Coordinator), and Makini Duewea
                      (Coach Mentor).
                    </p>
                    <p className="mb-0">
                      Designed to support prospective students in gaining clarity and confidence, these sessions are
                      held monthly, and attendees must pre-register. A recording is shared with all registrants.
                    </p>
                  </div>
                  <Button
                    href="https://events.zoom.us/eo/Akd7umtSEyVnJ9nd_Z1nCWhvDDuTtDxMWl_wPLfWxU2baYx5G1T9~AggLXsr32QYFjq8BlYLZ5I06Dg"
                    color="learn-more"
                    block
                    style={{borderTopLeftRadius: 0, borderTopRightRadius: 0}}
                    target="_blank"
                  >
                    Book Now
                  </Button>
                </div>
                {clarityCall && (
                  <div className="background-light-gray rounded box-shadow-black-50 mb-5">
                    <div className="p-4 p-lg-5">
                      <h3 className="section-heading">Clarity Calls</h3>
                      <p>
                        Book a 30-minute <b>one-on-one call with one of our Program Advisors</b>{' '}
                        <br className="d-none d-xl-block" />
                        for a personalized conversation.
                      </p>
                      <p>
                        You’ll have the chance to ask specific questions, explore your goals, and get support in
                        deciding if the PBCC program is the right fit for you. You can talk through things like
                        schedule, tuition, career opportunities, and how the training aligns with your vision, all in a
                        supportive, no-pressure environment.
                      </p>
                      <p className="mb-0">
                        If you’d like, you can also connect with a graduate to hear about their experience firsthand.
                      </p>
                    </div>
                    <Button
                      href="/calls/"
                      color="learn-more"
                      block
                      style={{borderTopLeftRadius: 0, borderTopRightRadius: 0}}
                      target="_blank"
                    >
                      Book Now
                    </Button>
                  </div>
                )}
                <div className="background-light-gray rounded p-4 p-lg-5 box-shadow-black-50">
                  <h3 className="section-heading">Direct Email Support</h3>
                  <p>
                    <b>Prefer to connect right away?</b> If you don’t want to wait for a call, feel free to email us
                    directly at{' '}
                    <b>
                      <a href="mailto:certificate@foodrevolution.org" target="_blank">
                        certificate@foodrevolution.org
                      </a>
                    </b>{' '}
                    for more immediate support.
                  </p>
                  <p className="mb-0">
                    We’re happy to answer your questions and help you get the clarity you need. Our team responds to
                    messages Monday through Friday, usually within 24 hours, and we’re here to support you however we
                    can.
                  </p>
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>
      </div>
      <div id="curriculum">
        <SectionCL id="pbcc-curriculum" color="light-gray" className="text-center">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-uppercase mb-4">Curriculum & Faculty</h2>
                <p>
                  We worked hard to create a dynamic and interactive learning experience for you. Our three-pillar
                  training consists of video/audio lessons, written transcripts for those who prefer to read, live Q&A
                  calls, live group Practice Pods, and helpful guides you can pin or print.
                </p>
                <p>
                  Pillars 1 (Nutrition) and 2 (Coaching) contain practice comprehension quizzes, helping you succeed in
                  the midterm and final exam. These exams are required to become an FRN-Certified Plant-Based Coach.
                  Pillar 3 (Impact) shows you how to market yourself so you can help impact more people, whether that be
                  through your own coaching practice, employment with a health organization, or simply as a leader in
                  your community.
                </p>
                <p className="mb-0">
                  Participation in Pillar 3 isn’t required to become certified. But you don’t want to miss all this
                  expert guidance on how to market your services so you can impact as many people as possible!
                </p>
                <Row className="align-items-stretch my-5">
                  <Col lg="6" className="d-flex flex-column flex-grow-1 mb-4 mb-lg-0">
                    <div className="background-white box-shadow-black-50 rounded d-flex flex-column flex-grow-1">
                      <div className="p-2 my-auto">
                        <img
                          src="https://cdn.foodrevolution.org/pbcc/pbcc-program-snapshot-2026.png"
                          alt="2026 program schedule graphic"
                          width=""
                          height=""
                          style={{maxHeight: '300px'}}
                        />
                      </div>
                      <ProgramScheduleButton />
                    </div>
                  </Col>
                  <Col lg="6" className="d-flex flex-column flex-grow-1">
                    <div className="background-white box-shadow-black-50 rounded d-flex flex-column flex-grow-1">
                      <div className="p-2 my-auto">
                        <img
                          src="https://cdn.foodrevolution.org/pbcc/images/pbcc-2026-curriculum-3dcover.png"
                          alt="2026 program curriculum cover"
                          width=""
                          height=""
                          style={{maxHeight: '300px'}}
                        />
                      </div>
                      <Button
                        href="https://cdn.foodrevolution.org/pbcc/PBCC%20-%202026%20-%20Program%20Curriculum.pdf"
                        target="_blank"
                        color="purple"
                        className="text-16 rounded-top-none"
                        block
                      >
                        <FontAwesomeIcon className="mr-2" icon={faCloudArrowDown} />
                        Download the 2026 Curriculum
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row className="align-items-stretch justify-content-center">
              {iconList.map((item, i) => (
                <Col xs="6" lg="4" className="d-flex flex-column mb-4">
                  <div className="text text-800 mb-3">{item.text}</div>
                  <div className="icon mt-auto">
                    <img src={item.icon} style={{maxHeight: '150px'}} />
                  </div>
                </Col>
              ))}
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="pbcc-questions" color="white" className="text-center">
          <Container>
            <Row>
              <Col>
                <h4 className="section-heading">
                  100% Online Access So You Can <br className="d-none d-xl-block" />
                  Complete The Course On Your Schedule
                </h4>
                <p>
                  Seamlessly weave your studies into your life with a combination of live, group sessions, and focused
                  exercises to do on your own. All live sessions are recorded, so you can absorb them any time you like,
                  anywhere you have an internet connection. You’ll have access to the training material even after you
                  graduate, so that you can return to it anytime.
                </p>
              </Col>
            </Row>
          </Container>
        </SectionCL>
      </div>
      <PillarModulesCondensed variant={variant} />
      <SectionCL id="pbcc-bringworld" color="black" className="text-center text-white">
        <Container>
          <Row>
            <Col>
              <h3 className="section-heading">Bring the food revolution to the world</h3>
              <Button color="cta" size="lg" spy={true} offset={-75} to="pbcc-cta-1" smooth={true} href="#" tag={Link}>
                Join Us
              </Button>
            </Col>
          </Row>
        </Container>
      </SectionCL>
      <div id="bonuses">
        <SectionCL id="pbcc-bonuses" color="light-gray">
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-uppercase text-center mb-5">Bonuses</h2>
                {bonus.map((bonus, i) => {
                  if (
                    (!frsSpecialBonus && bonus.id === 'bonusEmpowerment2126') ||
                    (frsSpecialBonus && bonus.id === 'bonusEmpowerment2526') ||
                    (!frsBonus && bonus.id === 'bonusEmpowerment2526') ||
                    (!wlcBonus && bonus.id === 'bonusWLC') ||
                    (!mastermindBonus && bonus.id === 'bonusMastermind')
                  ) {
                    return null;
                  }
                  return (
                    <div key={bonus.id} className="background-white p-4 p-lg-5 rounded box-shadow-black-50 mb-4">
                      {bonus.specialOffer && (
                        <h4 className="section-heading text-center text-green">{bonus.specialOffer}</h4>
                      )}
                      <h3 className="section-heading text-center mb-4">{bonus.title}</h3>
                      {bonus.image && bonus.image.url ? (
                        <div className="text-center">
                          <img
                            className="mb-4"
                            src={bonus.image.url}
                            alt={bonus.image.alt}
                            style={{maxHeight: '500px'}}
                          />
                        </div>
                      ) : null}
                      <ReactMarkdown source={bonus.description} />
                      <h6 className="mt-0">
                        <i>Value: {bonus.value}</i>
                      </h6>
                      {bonus.free && (
                        <h6 className="mt-0 text-400">
                          <i>{bonus.free}</i>
                        </h6>
                      )}
                      {bonus.id === 'bonusNextLevel' && bonus.faculty ? (
                        <>
                          <h5 className="section-heading mt-4">Guest Faculty & Lessons:</h5>
                          <Row className="align-items-stretch">
                            {bonus.faculty.map((item, i) => {
                              return (
                                <Col
                                  key={'faculty' + i}
                                  xs="12"
                                  md="6"
                                  xl="4"
                                  className="text-center d-flex flex-column flex-grow-1 mb-3"
                                >
                                  <div className="rounded background-light-gray-50 box-shadow-black-25 p-3 flex-grow-1 d-flex flex-column">
                                    <h5 className="section-heading my-auto">{item.lesson}</h5>
                                    {speakers
                                      .filter((speaker) => speaker.id === item.speaker)
                                      .map((speaker) => {
                                        return (
                                          <div className="mt-3">
                                            <img
                                              className="box-shadow-image rounded-circle mb-3"
                                              src={speaker.image}
                                              style={{maxHeight: '100px'}}
                                            />
                                            <p className="text-800 m-0">{speaker.name}</p>
                                            {speaker.bio ? (
                                              <PillarModal
                                                name={speaker.name}
                                                image={speaker.image}
                                                bio={speaker.bio}
                                                className="text-16"
                                              />
                                            ) : null}
                                          </div>
                                        );
                                      })}
                                  </div>
                                </Col>
                              );
                            })}
                          </Row>
                        </>
                      ) : null}
                      {bonus.id === 'bonusPlantrician' && (
                        <TestimonialBubble id="pbccChefAgatheMBonus" className="mt-4" />
                      )}
                    </div>
                  );
                })}
                {bonusesValue && (
                  <h3 className="text-center">
                    Total Value of Bonuses: <span className="text-green">Over {bonusesValue}</span>
                  </h3>
                )}
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL
          id="pbcc-revolutionize"
          color="white"
          imgPosition="right"
          imgDisplayStyle="rectangle"
          imgBgAlignmentMobile="center bottom"
          imgBgAlignmentDesktop="right bottom"
          imgUrl="https://cdn.foodrevolution.org/pbcc/images/685863898-rect.jpg"
          angleBottomDisplay="none"
        >
          <h3 className="section-heading">
            Use Your Plant-Based Coaching Certificate to Revolutionize the Way the World Eats
          </h3>
          <p>Gain the skills you need to step onto your path with clarity and confidence. You could:</p>
          <ListIcon>
            <>Fuel the movement to help more people achieve their health goals</>
            <>Start a podcast, become a world-class speaker, or write a book to reach thousands of people at once</>
            <>Support yourself, your family, and your future, and have a better work-life balance</>
            <>
              Work with clients from anywhere in the world (with the freedom to travel for months each year if you
              want!)
            </>
            <>Become an outspoken advocate for change within the food industry</>
            <>Advocate for wellness in disadvantaged populations</>
            <>Create a ripple effect of healing within entire families and communities</>
            <>
              Be a vital part of the solution to climate change, deforestation, species extinction, and animal cruelty
            </>
          </ListIcon>
        </SectionCL>
        <SectionCL id="pbcc-includes" color="white" innerClass="pt-0">
          <Container>
            <Row>
              <Col>
                <h3 className="section-heading text-center text-green mb-3">
                  Food Revolution Network <br className="d-none d-xl-block" />
                  Plant-Based Coaching Certificate Includes
                </h3>
                <div className="d-flex justify-content-center">
                  <ListIcon className="m-0">
                    <>
                      Unlimited access to more than 75 lessons in Nutrition, Coaching, and Impact, all taught by top
                      experts
                    </>
                    <>
                      More than 200 handouts, templates, and guides so you have everything you need right at your
                      fingertips
                    </>
                    <>Live Q&A and training calls so you can get all your questions answered and clear any confusion</>
                    <>Live Practice Pods and Impact Sessions so you can start coaching right in the program!</>
                    <>Community Forum where you can connect with your peers, moderated by our team of experts</>
                    <>Certificate upon Successful Completion to help build your credibility and attract clients</>
                    <>Qualifying graduates receive 24 CE/CPD credits through NBHWC and CPD Standards UK</>
                    {wlcBonus && (
                      <>
                        <b className="text-green">Limited-Time Bonus:</b> Unlimited access to WHOLE Life Club
                      </>
                    )}
                    {frsBonus && (
                      <>
                        {frsSpecialBonus ? (
                          <>
                            <b className="text-green">Limited-Time Bonus:</b> 2021–2026
                          </>
                        ) : (
                          <>
                            <b className="text-green">Bonus:</b> 2025 and 2026
                          </>
                        )}{' '}
                        Food Revolution Summit Empowerment Packages
                      </>
                    )}
                    {mastermindBonus && (
                      <>
                        <b className="text-green">Limited-Time Bonus:</b> Access to Ocean Robbins’ Food Revolution
                        Mastermind
                      </>
                    )}
                    <>
                      <b className="text-green">Bonus:</b> Next Level Collection
                    </>
                    <>
                      <b className="text-green">Bonus:</b> Unlocking Longevity
                    </>
                    <>
                      <b className="text-green">Bonus:</b> Qualifying graduates receive one free year of professional
                      listing in Plantrician Project’s directory of plant-based healthcare providers
                    </>
                    <>
                      <b className="text-green">Bonus:</b> Discounted pricing for Coaches Console
                    </>
                    <>
                      <b className="text-green">Bonus:</b> Discounted pricing on Root-Cause Health Coaching
                    </>
                  </ListIcon>
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="pbcc-competitive" color="black" className="text-center text-white">
          <Container>
            <Row>
              <Col>
                <h4 className="section-heading text-400">
                  We’ve created a competitively priced, robust curriculum to help you establish and grow your niche,
                  increase your impact, and be a potent leader in the food revolution movement.
                </h4>
                <Button color="cta" size="lg" spy={true} offset={-75} to="pbcc-cta-1" smooth={true} href="#" tag={Link}>
                  Join Us
                </Button>
              </Col>
            </Row>
          </Container>
        </SectionCL>
      </div>
      <SectionCL id="pbcc-faq" color="white">
        <Container>
          <Row>
            <Col>
              <h2 className="section-heading">Frequently Asked Questions</h2>
              <FaqList clarityCall={clarityCall} phaseIteration={phaseIteration} />
            </Col>
          </Row>
        </Container>
      </SectionCL>
      {ctaVisible && (
        <SectionCL id="pbcc-cta-3" color="purple">
          <Container>
            <Row>
              <Col>
                <div className="background-white rounded box-shadow-black-50 p-4 p-lg-5 text-center">
                  <ApplyNowCta />
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>
      )}
      {clarityCall && <ClarityCall id="pbcc-clarity-3" bgColor="black" />}
      <SectionCL id="pbcc-references" color="light-gray" angle="none" order="last">
        <Container>
          <Row>
            <Col>
              <h3 className="section-heading">References</h3>
              <ListReferences>
                {references.map((reference, i) => (
                  <ListReferenceItem number={i + 1} text={reference.link} />
                ))}
              </ListReferences>
            </Col>
          </Row>
        </Container>
      </SectionCL>
    </>
  );
}
